import { Routes } from '@angular/router';
import { permissionGuard } from './core/guards/permission.guard';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { PERMISSION_LIST } from '@/core/constants/constants';
import { nonAuthGuard } from '@/core/guards/non-auth.guard';
import { authGuard } from '@/core/guards/auth-guard';

export const routes: Routes = [
  // 🔐 AUTH
  {
    path: '',
    component: AuthLayoutComponent,
    canActivateChild: [nonAuthGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('./core/auth/auth.routes').then((m) => m.AuthRoutes),
      },
    ],
  },

  // Client Dashboard (Public - No Auth Required)
  {
    path: 'client-dashboard',
    loadChildren: () =>
      import('./modules/client-dashboard/client-dashboard.routes').then(
        (m) => m.clientDashboardRoutes,
      ),
  },

  // 🏠 MAIN APP
  {
    path: '',
    component: MainLayoutComponent,
    canActivateChild: [authGuard, permissionGuard],
    children: [
      // Dashboard
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./modules/dashboard/dashboard.routes').then((m) => m.dashboardRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.DASHBOARD.VIEW] },
      },

      // Super Admin Dashboard
      {
        path: 'super-admin-dashboard',
        loadChildren: () =>
          import('./modules/super-admin-dashboard/super-admin-dashboard.routes').then(
            (m) => m.superAdminDashboardRoutes,
          ),
        data: { PERMISSION_LIST: [PERMISSION_LIST.DASHBOARD.SUPER_ADMIN] },
      },

      // Client Lead Dashboard
      // {
      //   path: 'client-lead-dashboard',
      //   loadChildren: () =>
      //     import('./modules/client-lead-dashboard/client-lead-dashboard.routes').then(
      //       (m) => m.clientLeadDashboardRoutes,
      //     ),
      // },

      // Internal Lead Dashboard (Admin/Internal only) - EPIC 10 / Story 15
      {
        path: 'internal-lead-dashboard',
        loadChildren: () =>
          import('./modules/internal-lead-dashboard/internal-lead-dashboard.routes').then(
            (m) => m.internalLeadDashboardRoutes,
          ),
        data: { PERMISSION_LIST: [PERMISSION_LIST.ADMIN.ACCESS] },
      },

      // 🌍 Location
      // Country master hidden from the UI for now.
      // {
      //   path: 'countries',
      //   loadChildren: () =>
      //     import('./modules/countries/countries.routes').then((m) => m.countryRoutes),
      // },
      {
        path: 'states',
        loadChildren: () => import('./modules/states/states.routes').then((m) => m.stateRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.LOCATION.STATE_VIEW] },
      },
      {
        path: 'cities',
        loadChildren: () => import('./modules/cities/cities.routes').then((m) => m.cityRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.LOCATION.CITY_VIEW] },
      },

      // 🔐 Access Control
      {
        path: 'users',
        loadChildren: () => import('./modules/users/users.routes').then((m) => m.userRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.USER.VIEW] },
      },
      {
        path: 'roles',
        loadChildren: () => import('./modules/roles/roles.routes').then((m) => m.roleRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.ROLE.VIEW] },
      },

      // 🏢 Organization
      {
        path: 'branches',
        loadChildren: () =>
          import('./modules/branches/branches.routes').then((m) => m.branchRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.BRANCH.VIEW] },
      },
      {
        path: 'venues',
        loadChildren: () => import('./modules/venues/venues.routes').then((m) => m.venueRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.VENUE.VIEW] },
      },

      // 💼 Business
      {
        path: 'clients',
        loadChildren: () => import('./modules/clients/clients.routes').then((m) => m.clientRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.CLIENT.VIEW] },
      },
      {
        path: 'projects',
        loadChildren: () =>
          import('./modules/projects/projects.routes').then((m) => m.projectRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.PROJECT.VIEW] },
      },
      {
        path: 'vendors',
        loadChildren: () => import('./modules/vendors/vendors.routes').then((m) => m.vendorRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.VENDOR.VIEW] },
      },
      {
        path: 'expenses',
        loadChildren: () =>
          import('./modules/expenses/expenses.routes').then((m) => m.expenseRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.EXPENSES.VIEW] },
      },
      {
        path: 'expense-categories',
        loadChildren: () =>
          import('./modules/expense-categories/expense-categories.routes').then(
            (m) => m.expenseCategoryRoutes,
          ),
        data: { PERMISSION_LIST: [PERMISSION_LIST.EXPENSE_CATEGORY.VIEW] },
      },

      // 🏠 Properties
      {
        path: 'apartments',
        loadChildren: () =>
          import('./modules/apartments/apartments.routes').then((m) => m.apartmentRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.APARTMENT.VIEW] },
      },

      // 🔄 Activations
      {
        path: 'activations',
        loadChildren: () =>
          import('./modules/activations/activations.routes').then((m) => m.activationRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.ACTIVATION.VIEW] },
      },
      {
        path: 'activation-assignment',
        loadChildren: () =>
          import('./modules/activation-assignment/activation-assignment.routes').then(
            (m) => m.activationAssignRoutes,
          ),
        data: { PERMISSION_LIST: [PERMISSION_LIST.ACTIVATION_ASSIGNMENT.VIEW] },
      },
      {
        path: 'activation-promoters',
        loadChildren: () =>
          import('./modules/activation-promoters/activation-promoters.routes').then(
            (m) => m.activationPromoterRoutes,
          ),
        data: { PERMISSION_LIST: [PERMISSION_LIST.ACTIVATION_PROMOTER.VIEW] },
      },
      {
        path: 'promoters',
        loadChildren: () =>
          import('./modules/promoters/promoters.routes').then((m) => m.promoterRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.ACTIVATION_PROMOTER.VIEW] },
      },

      // 📦 Materials & Inventory
      {
        path: 'materials',
        loadChildren: () =>
          import('./modules/materials/materials.routes').then((m) => m.materialsRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.MATERIAL.VIEW] },
      },
      {
        path: 'inventories',
        loadChildren: () =>
          import('./modules/inventorys/inventorys.routes').then((m) => m.inventoryRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.INVENTORY.VIEW] },
      },
      {
        path: 'material-purchases',
        loadChildren: () =>
          import('./modules/material-purchases/material-purchases.routes').then(
            (m) => m.materialPurchaseRoutes,
          ),
        data: { PERMISSION_LIST: [PERMISSION_LIST.INVENTORY.VIEW] },
      },

      // 📢 Promotions
      {
        path: 'promotion-channels',
        loadChildren: () =>
          import('./modules/promotion-channels/promotion-channels.routes').then(
            (m) => m.promotionalChannelRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.PROMOTION_CHANNEL.VIEW],
        },
      },
      {
        path: 'promotion-durations',
        loadChildren: () =>
          import('./modules/promotion-durations/promotion-durations.routes').then(
            (m) => m.promotionalDurationRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.PROMOTION_DURATION.VIEW],
        },
      },
      {
        path: 'sources',
        loadChildren: () => import('./modules/sources/sources.routes').then((m) => m.sourceRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.SOURCE.VIEW] },
      },
      {
        path: 'positions',
        loadChildren: () =>
          import('./modules/positions/positions.routes').then((m) => m.positionRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.POSITION.VIEW] },
      },
      {
        path: 'disable-reasons',
        loadChildren: () =>
          import('./modules/disable-reasons/disable-reasons.routes').then(
            (m) => m.disableReasonRoutes,
          ),
        data: { PERMISSION_LIST: [PERMISSION_LIST.DISABLE_REASON.VIEW] },
      },
      {
        path: 'promoters',
        loadChildren: () =>
          import('./modules/promoters/promoters.routes').then((m) => m.promoterRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.ACTIVATION_PROMOTER.VIEW] },
      },
      // 📈 Leads
      {
        path: 'leads',
        loadChildren: () => import('./modules/leads/leads.routes').then((m) => m.leadsRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.LEAD.VIEW] },
      },

      // 👥 HR - Applicants
      {
        path: 'applicants',
        loadChildren: () =>
          import('./modules/applicants/applicants.routes').then((m) => m.applicantsRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.APPLICANT.VIEW] },
      },
      {
        path: 'feedbacks',
        loadChildren: () =>
          import('./modules/feedback/feedback.routes').then((m) => m.feedBackRoutes),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.FEEDBACK.VIEW, PERMISSION_LIST.FEEDBACK.VIEW_ALL],
        },
      },
      {
        path: 'tech-parks',
        loadChildren: () =>
          import('./modules/tech-parks/techparks.routes').then((m) => m.techParkRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.TECH_PARK.VIEW] },
      },

      {
        path: 'malls',
        loadChildren: () => import('./modules/malls/malls.routes').then((m) => m.mallsRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.MALL.VIEW] },
      },
      {
        path: 'hoardings',
        loadChildren: () =>
          import('./modules/hoardings/hoardings.routes').then((m) => m.hoardingRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.HOARDING.VIEW] },
      },
    ],
  },
  { path: '**', redirectTo: 'admin/login' },
];
